import { Chat, WhatsappContact } from "@bt-common/interfaces/whatsapp-types";
import { WaContact } from "@common-models/waContact";

/**
 * Contact-like object that can be used to extract a display name.
 * Supports both WhatsappContact and WaContact interfaces.
 */
export interface ContactLike {
    name?: string;
    displayName?: string;
    pushname?: string;
    shortName?: string;
    notifyName?: string;
    notify?: string;
    formattedTitle?: string;
    formattedName?: string;
    verifiedName?: string;
    waName?: string;
    firstName?: string;
    lastName?: string;
    id?: string | { _serialized?: string; user?: string };
    _serialized?: string;
    number?: string;
}

/**
 * Chat-like object that can be used to extract a display name.
 */
export interface ChatLike {
    name?: string;
    displayName?: string;
    formattedTitle?: string;
    contact?: Partial<ContactLike>;
    id?: string | { _serialized?: string; user?: string };
}

/**
 * Options for getWaChatContactDisplayName function.
 */
export interface DisplayNameOptions {
    /** If true, prefixes pushname with "~ " (WhatsApp convention for non-saved contacts) */
    prefixPushname?: boolean;
    /** Fallback value if no name can be determined */
    fallback?: string;
    /** If true, formats the phone number as a fallback instead of using raw number */
    formatPhoneNumber?: boolean;
}

/**
 * Formats a phone number for display (removes @c.us/@g.us suffix).
 */
function formatPhoneForDisplay(phoneId: string | undefined): string {
    if (!phoneId) return '';
    // Remove WhatsApp suffixes
    const cleaned = phoneId.replace(/@c\.us$|@g\.us$|@s\.whatsapp\.net$/, '');
    // Add + prefix if it looks like a phone number
    if (/^\d+$/.test(cleaned)) {
        return `+${cleaned}`;
    }
    return cleaned;
}

/**
 * Extracts the phone number/id from various object formats.
 */
function extractPhoneId(obj: ChatLike | ContactLike | undefined): string | undefined {
    if (!obj) return undefined;

    // Try _serialized directly
    if ('_serialized' in obj && obj._serialized) {
        return obj._serialized;
    }

    // Try id field (can be string or object)
    if (obj.id) {
        if (typeof obj.id === 'string') {
            return obj.id;
        }
        if (obj.id._serialized) {
            return obj.id._serialized;
        }
        if (obj.id.user) {
            return obj.id.user;
        }
    }

    // Try number field
    if ('number' in obj && obj.number) {
        return obj.number;
    }

    return undefined;
}

/**
 * Gets the display name from a contact-like object.
 */
function getContactDisplayName(contact: Partial<ContactLike> | undefined, options: DisplayNameOptions): string | undefined {
    if (!contact) return undefined;

    // Priority order for contact names:
    // 1. name (user-saved name)
    // 2. displayName
    // 3. formattedName
    // 4. waName
    // 5. verifiedName (business accounts)
    // 6. notifyName/notify
    // 7. pushname (with optional prefix)
    // 8. shortName
    // 9. formattedTitle
    // 10. firstName + lastName

    if (contact.name) return contact.name;
    if (contact.displayName) return contact.displayName;
    if (contact.formattedName) return contact.formattedName;
    if (contact.waName) return contact.waName;
    if (contact.verifiedName) return contact.verifiedName;
    if (contact.notifyName) return contact.notifyName;
    if (contact.notify) return contact.notify;

    if (contact.pushname) {
        return options.prefixPushname ? `~ ${contact.pushname}` : contact.pushname;
    }

    if (contact.shortName) return contact.shortName;
    if (contact.formattedTitle) return contact.formattedTitle;

    // Try constructing from firstName/lastName
    if (contact.firstName || contact.lastName) {
        const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
        if (fullName) return fullName;
    }

    return undefined;
}

/**
 * Unified function to get the display name from a Chat or Contact object.
 *
 * This function consolidates all the different display name extraction patterns
 * used throughout the codebase into a single, consistent implementation.
 *
 * Priority order:
 * 1. Chat-level names (name, displayName, formattedTitle)
 * 2. Contact-level names from chat.contact
 * 3. Phone number as fallback
 *
 * @param chatOrContact - A Chat object, Contact object, or partial of either
 * @param options - Optional configuration for name extraction
 * @returns The display name, or the fallback value if no name could be determined
 *
 * @example
 * // Basic usage with a chat
 * const name = getWaChatContactDisplayName(currentChat);
 *
 * @example
 * // With options
 * const name = getWaChatContactDisplayName(contact, {
 *   prefixPushname: true,
 *   fallback: 'Unknown Contact'
 * });
 */
export function getWaChatContactDisplayName(
    chatOrContact: Partial<ChatLike> | Partial<ContactLike> | Chat | WhatsappContact | WaContact | undefined | null,
    options: DisplayNameOptions = {}
): string {
    const { fallback = '', formatPhoneNumber: shouldFormatPhone = true } = options;

    if (!chatOrContact) return fallback;

    const obj = chatOrContact as Partial<ChatLike & ContactLike>;

    // First, try chat-level names
    if (obj.name) return obj.name;
    if (obj.displayName) return obj.displayName;
    if (obj.formattedTitle) return obj.formattedTitle;

    // Try contact-level names (works for both Chat.contact and direct Contact objects)
    const contactName = getContactDisplayName(obj, options);
    if (contactName) return contactName;

    // If this is a Chat with a nested contact, try the contact's names
    if ('contact' in obj && obj.contact) {
        const nestedContactName = getContactDisplayName(obj.contact, options);
        if (nestedContactName) return nestedContactName;
    }

    // Fallback to phone number
    const phoneId = extractPhoneId(obj) || ('contact' in obj ? extractPhoneId(obj.contact as ContactLike) : undefined);
    if (phoneId) {
        return shouldFormatPhone ? formatPhoneForDisplay(phoneId) : phoneId;
    }

    return fallback;
}

/**
 * Gets display name specifically optimized for chat sidebar/header display.
 * Limits the name length and provides sensible defaults.
 *
 * @param chat - The chat object
 * @param maxLength - Maximum length before truncation (default: 20)
 * @returns The truncated display name
 */
export function getChatDisplayNameForHeader(
    chat: Partial<ChatLike> | undefined | null,
    maxLength: number = 20
): string {
    const name = getWaChatContactDisplayName(chat, { fallback: '' });
    if (!name) return '';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
}
